import { Controller } from '@hotwired/stimulus';

import {
    trans,

    LISTING_FILTER_PRICE,
    LISTING_FILTER_PRICE_BUY,
    LISTING_FILTER_PRICE_RENT,
} from '../translator';


export default class ListingsFilterController extends Controller {
    static values = {
        dataUrl:    String,
        withLabels: Boolean,

        withCountries: Boolean,
    };

    static targets = [
        'form',

        'priceTo',
        'countries',
        'states',
        'districts',
        'zipCities',
    ];

    /**
     * @return {string}
     */
    get dataUrl() {
        return this.dataUrlValue;
    }

    /**
     * @return {boolean}
     */
    get withLabels() {
        return this.withLabelsValue;
    }

    /**
     * @return {boolean}
     */
    get withCountries() {
        return this.withCountriesValue;
    }

    /**
     * @returns {HTMLElement}
     */
    get form() {
        return this.formTarget;
    }

    /**
     * @returns {HTMLElement|null}
     */
    get priceTo() {
        return this.hasPriceToTarget ? this.priceToTarget : null;
    }

    /**
     * @returns {HTMLElement|null}
     */
    get countries() {
        return this.hasCountriesTarget ? this.countriesTarget : null;
    }

    /**
     * @returns {HTMLElement|null}
     */
    get states() {
        return this.hasStatesTarget ? this.statesTarget : null;
    }

    /**
     * @returns {HTMLElement|null}
     */
    get districts() {
        return this.hasDistrictsTarget ? this.districtsTarget : null;
    }

    /**
     * @returns {HTMLElement|null}
     */
    get zipCities() {
        return this.hasZipCitiesTarget ? this.zipCitiesTarget : null;
    }


    connect() {
        this.form.addEventListener('formdata', this._formData);
    }

    disconnect() {
        this.form.removeEventListener('formdata', this._formData);
    }

    changeBuyRentAll(event) {
        const value = event.currentTarget.value;

        let text;
        if (value === 'buy') {
            text = trans(LISTING_FILTER_PRICE_BUY);
        } else if (value === 'rent') {
            text = trans(LISTING_FILTER_PRICE_RENT);
        } else {
            text = trans(LISTING_FILTER_PRICE);
        }

        if (this.withLabels) {
            const label = this.element.querySelector(`label[for="${this.priceTo.id}"]`);
            if (label) {
                label.innerHTML = text;
            }

        } else {
            const priceTo = this.priceTo;

            if (priceTo) {
                priceTo.placeholder = text;
            }
        }
    }

    /**
     * @param {FormDataEvent} event
     * @private
     */
    _formData = (event) => {
        const formData = event.formData;
        this._cleanFormData(formData);
    };

    /**
     * @param {FormData} formData
     * @private
     */
    _cleanFormData(formData) {
        const imasks = {};
        for (const el of this.form.querySelectorAll('[data-imask]')) {
            imasks[el.name] = el;
        }

        // iterating a new array because looping and changing the same iterator doesn't yield all entries
        const entries = Array.from(formData.entries());

        for (const [key, value] of entries) {
            if (value === '' || value === null) {
                formData.delete(key);
            } else if (key in imasks) {
                const unmasked = imasks[key].imask?.unmaskedValue;
                if (unmasked !== '' && unmasked !== null) {
                    formData.set(key, unmasked);
                }
            }
        }
    }

    async updateDependentFields(event) {
        const element = event.currentTarget;

        const states    = this.states;
        const districts = this.districts;
        const zipCities = this.zipCities;

        const updateStates = states
            && element !== states
            && element !== districts
            && element !== zipCities;

        const updateDistricts = districts
            && element !== districts
            && element !== zipCities;

        const updateZipCities = zipCities
            && element !== zipCities;

        if (!updateStates && !updateDistricts && !updateZipCities) {
            return;
        }

        if (updateStates) {
            states.tomselect.disable();
        }

        if (updateDistricts) {
            districts.tomselect.disable();
        }

        if (updateZipCities) {
            zipCities.tomselect.disable();
        }

        const formData = new FormData(this.form);
        this._cleanFormData(formData);

        // don't build huge selects for all countries by default (if countries select exists)
        if (this.withCountries && !formData.get(this.countries.name)) {
            if (states) {
                this._updateSelectData(states, []);
            }

            if (districts) {
                this._updateSelectData(districts, []);
            }

            if (zipCities) {
                this._updateSelectData(zipCities, []);
            }

            return;
        }


        const requestData = new URLSearchParams();
        for (const [key, value] of formData.entries()) {
            requestData.append(key, value);
        }

        let data;
        try {
            const response = await fetch(this.dataUrl, {
                method:  'post',
                body:    requestData,
                headers: {
                    'Accept':       'application/json',
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });

            data = await response.json();
        } catch (e) {
            return;
        }


        if (updateStates) {
            this._updateSelectData(states, data.states);
        }

        if (updateDistricts) {
            this._updateSelectData(districts, data.districts);
        }

        if (updateZipCities) {
            this._updateSelectData(zipCities, data.zipCities);
        }

        states?.tomselect?.enable();
        districts?.tomselect?.enable();
        zipCities?.tomselect?.enable();
    }

    /**
     * @param {HTMLSelectElement} select
     * @param {Object} data
     * @private
     */
    _updateSelectData(select, data) {
        const tomselect = select.tomselect;

        if (!tomselect) {
            // probably not yet initialized?
            return;
        }

        tomselect.clearOptions();

        const dataValues = Object.values(data);

        for (const value of tomselect.getValue()) {
            if (dataValues.indexOf(value) === -1) {
                tomselect.removeOption(value);
            }
        }

        for (let index = select.options.length; index >= 0; index--) {
            select.remove(index);
        }

        for (const [text, value] of Object.entries(data)) {
            const option = document.createElement('option');
            option.text  = text;
            option.value = value;
            select.add(option);

            tomselect.addOption({
                value, text
            });
        }
    }
}
